'use client';

import { Cross2Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Button } from '@registry/new-york/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@registry/new-york/ui/popover';
import { Column } from '@tanstack/react-table';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import styles from './DataTableSearchColumnHeader.module.scss';
import { TruncatedHeaderTitle } from './TruncatedHeaderTitle/TruncatedHeaderTitle';

interface DataTableSearchColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  hideInlineValue?: boolean;
  disableFilter?: boolean;
  /** Ellipsizes long titles; hovering reveals the full title over neighboring columns. */
  truncate?: boolean;
}

const DEBOUNCE_MS = 600;

export const DataTableSearchColumnHeader = <TData, TValue>({
  column,
  title,
  className,
  hideInlineValue = false,
  disableFilter = false,
  truncate = false,
}: DataTableSearchColumnHeaderProps<TData, TValue>) => {
  const [open, setOpen] = useState(false);

  const appliedFilter = (column.getFilterValue() as string[] | undefined)?.[0] ?? '';
  const isFiltered = Boolean(appliedFilter);

  const [draft, setDraft] = useState(appliedFilter);
  const inputRef = useRef<HTMLInputElement>(null);

  const prevAppliedRef = useRef(appliedFilter);
  useEffect(() => {
    if (prevAppliedRef.current !== appliedFilter) {
      prevAppliedRef.current = appliedFilter;
      if (!open) {
        setDraft(appliedFilter);
      }
    }
  }, [appliedFilter, open]);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return undefined;
    }
    const timeout = setTimeout(() => {
      if (draft) {
        column.setFilterValue([draft]);
      } else {
        column.setFilterValue(undefined);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
    // column is stable; intentionally excluded to avoid re-firing on filter state update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const clear = () => {
    setDraft('');
    column.setFilterValue(undefined);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const titleNode = (
    <TruncatedHeaderTitle
      title={title}
      truncate={truncate}
    />
  );

  if (disableFilter) {
    return <div className={clsx(styles.header, truncate && styles.headerTruncate, className)}>{titleNode}</div>;
  }

  return (
    <div className={clsx(styles.header, truncate && styles.headerTruncate, className)}>
      <Popover
        open={open}
        onOpenChange={handleOpenChange}
      >
        <PopoverTrigger
          asChild
          className={clsx(truncate && styles.triggerTruncate)}
        >
          <Button
            variant="ghost"
            size="sm"
            className={clsx(styles.trigger, truncate && styles.buttonTruncate, isFiltered && styles.filtered)}
            data-filter-chip={isFiltered ? 'active' : 'idle'}
          >
            <MagnifyingGlassIcon className={clsx(styles.icon, isFiltered && styles.filtered)} />
            {titleNode}
            {isFiltered && !hideInlineValue && <span className={styles.filterBadge}>{appliedFilter}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={styles.popoverContent}
          align="start"
        >
          <input
            ref={inputRef}
            placeholder={`Search ${title}...`}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setDraft(appliedFilter);
                setOpen(false);
              }
            }}
            className={styles.input}
          />
          {(isFiltered || draft) && (
            <Button
              variant="ghost"
              size="sm"
              className={styles.clearButton}
              onClick={() => {
                clear();
                setOpen(false);
              }}
            >
              <Cross2Icon className={styles.clearIcon} />
              Clear filter
            </Button>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
